import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, UserIcon, UsersIcon, LogOutIcon, SettingsIcon, HelpCircleIcon, MonitorIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
import { WelcomeScreen } from '../components/WelcomeScreen';
import { SoloDashboard } from '../components/SoloDashboard';
import { MeetingDashboard } from '../components/MeetingDashboard';
import { ButlerCall } from '../components/ButlerCall';
import { VoiceControl } from '../components/VoiceControl';
import { Screensaver } from '../components/Screensaver';
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
export function CEODen() {
  const [showWelcome, setShowWelcome] = useState(true);
  const [isAlone, setIsAlone] = useState(true);
  const [guestCount, setGuestCount] = useState(0);
  const [showButler, setShowButler] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [screensaverActive, setScreensaverActive] = useState(false);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ceoName = 'Akshay Gupta';
  // Inactivity detection
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (!screensaverActive) {
      inactivityTimer.current = setTimeout(() => {
        setScreensaverActive(true);
      }, INACTIVITY_TIMEOUT);
    }
  }, [screensaverActive]);
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);
  const handleWelcomeComplete = useCallback(() => {
    setShowWelcome(false);
  }, []);
  const toggleVoice = useCallback(() => {
    setIsVoiceActive(prev => !prev);
  }, []);
  const dismissScreensaver = useCallback(() => {
    setScreensaverActive(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);
  const toggleMode = () => {
    if (isAlone) {
      setIsAlone(false);
      setGuestCount(2);
    } else {
      setIsAlone(true);
      setGuestCount(0);
    }
  };
  return <div className="h-screen w-screen bg-[#0D0F14] bg-texture overflow-hidden flex flex-col" data-id="element-682">
      {/* Welcome Screen */}
      <AnimatePresence data-id="element-683">
        {showWelcome && <WelcomeScreen ceoName={ceoName} onComplete={handleWelcomeComplete} data-id="element-684" />}
      </AnimatePresence>

      {/* Screensaver */}
      <Screensaver isActive={screensaverActive} onDismiss={dismissScreensaver} data-id="element-685" />

      {/* Main Interface */}
      {!showWelcome && <>
          {/* Top Bar */}
          <div className="p-5 pb-0 flex-shrink-0" data-id="element-686">
            <TopBar ceoName={ceoName} isAlone={isAlone} guestCount={guestCount} data-id="element-687" />
          </div>

          {/* Dashboard Content */}
          <AnimatePresence mode="wait" data-id="element-688">
            {isAlone ? <SoloDashboard key="solo" data-id="element-689" /> : <MeetingDashboard key="meeting" data-id="element-690" />}
          </AnimatePresence>

          {/* Bottom Action Bar */}
          <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="p-5 pt-0 flex-shrink-0" data-id="element-691">
            <div className="rounded-2xl px-6 py-3.5 flex items-center justify-between" style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(201,168,76,0.15)'
        }} data-id="element-692">
              {/* Left Actions */}
              <div className="flex items-center gap-3" data-id="element-693">
                <button onClick={() => setShowButler(true)} className="h-12 px-5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all hover:shadow-lg" style={{
              background: 'linear-gradient(135deg, #C9A84C, #D4AF37)',
              color: '#0D0F14',
              boxShadow: '0 0 0 0 rgba(201,168,76,0)'
            }} data-id="element-694">
                  <BellIcon className="w-4 h-4" data-id="element-695" />
                  Call Butler
                </button>

                <button onClick={toggleMode} className="h-12 px-5 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.09)] text-gray-300 font-medium text-sm transition-colors flex items-center gap-2" data-id="element-696">
                  {isAlone ? <>
                      <UsersIcon className="w-4 h-4" data-id="element-697" /> Guest Mode
                    </> : <>
                      <UserIcon className="w-4 h-4" data-id="element-698" /> Private Mode
                    </>}
                </button>

                {/* Screensaver trigger */}
                <button onClick={() => setScreensaverActive(true)} className="h-12 px-5 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.09)] text-gray-300 font-medium text-sm transition-colors flex items-center gap-2" data-id="element-699">
                  <MonitorIcon className="w-4 h-4" data-id="element-700" />
                  Screensaver
                </button>
              </div>

              {/* Center — Status */}
              <div className="flex items-center gap-5 text-xs text-gray-400" data-id="element-701">
                <div className="flex items-center gap-1.5" data-id="element-702">
                  <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-703" />
                  <span data-id="element-704">All Systems Online</span>
                </div>
                <div className="h-3 w-px bg-[rgba(255,255,255,0.1)]" data-id="element-705" />
                <div className="flex items-center gap-1.5" data-id="element-706">
                  <div className="w-2 h-2 rounded-full bg-blue-400" data-id="element-707" />
                  <span data-id="element-708">Network: Secure</span>
                </div>
                <div className="h-3 w-px bg-[rgba(255,255,255,0.1)]" data-id="element-709" />
                <div className="flex items-center gap-1.5" data-id="element-710">
                  <div className="w-2 h-2 rounded-full bg-[#C9A84C]" data-id="element-711" />
                  <span data-id="element-712">Voice: Ready</span>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-2" data-id="element-713">
                <button className="w-12 h-12 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.09)] flex items-center justify-center transition-colors" data-id="element-714">
                  <HelpCircleIcon className="w-5 h-5 text-gray-400" data-id="element-715" />
                </button>
                <button className="w-12 h-12 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.09)] flex items-center justify-center transition-colors" data-id="element-716">
                  <SettingsIcon className="w-5 h-5 text-gray-400" data-id="element-717" />
                </button>
                <button className="w-12 h-12 rounded-xl bg-[rgba(239,68,68,0.1)] hover:bg-[rgba(239,68,68,0.18)] border border-[rgba(239,68,68,0.2)] flex items-center justify-center transition-colors" data-id="element-718">
                  <LogOutIcon className="w-5 h-5 text-red-400" data-id="element-719" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Qubix Branding */}
          <div className="absolute bottom-20 right-6 text-[#C9A84C] opacity-35 text-xs tracking-[0.35em] font-light pointer-events-none" data-id="element-720">
            Powered by QUBIX
          </div>

          {/* Butler Call Modal */}
          <ButlerCall isOpen={showButler} onClose={() => setShowButler(false)} data-id="element-721" />

          {/* Voice Control */}
          <VoiceControl isListening={isVoiceActive} onToggle={toggleVoice} data-id="element-722" />
        </>}
    </div>;
}